import { useEffect, useRef, useState } from "react";

interface Options {
  duration?: number;
  start?: boolean;
}

const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export function useCountUp(target: number, { duration = 1400, start = true }: Options = {}): number {
  const [value, setValue] = useState(0);
  const valueRef = useRef(0);
  const reduce = useRef(false);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      reduce.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  }, []);

  useEffect(() => {
    const apply = (v: number) => {
      valueRef.current = v;
      setValue(v);
    };
    if (!start) return;
    if (!Number.isFinite(target) || reduce.current) {
      apply(target);
      return;
    }
    const from = valueRef.current;
    if (from === target) return;
    // First reveal plays the full count-up; later target changes (live
    // data refreshes) glide from the current value in a short ease-out
    // so numbers never snap. Mid-animation retargets also start from
    // the current value because cleanup cancels the previous frame.
    const first = !hasAnimatedRef.current;
    hasAnimatedRef.current = true;
    const runDuration = first ? duration : Math.min(duration, 600);
    const ease = first ? easeOutExpo : easeOutCubic;
    let raf = 0;
    const t0 = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / runDuration);
      apply(from + (target - from) * ease(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);

  return value;
}
